import { useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useEstados, useCidades } from "@/hooks/useEstadosCidades";
import { useNavigate } from "react-router-dom";

export default function LandingLocationSelector() {
  const navigate = useNavigate();
  const estados = useEstados();
  const [selectedEstado, setSelectedEstado] = useState("");
  const [selectedCidade, setSelectedCidade] = useState("");
  const { cidades, loading } = useCidades(selectedEstado);

  const slugify = (text: string) =>
    text.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const handleSearch = () => {
    if (!selectedEstado) {
      navigate("/mapa");
      return;
    }

    const estadoSlug = slugify(selectedEstado);
    const cidadeSlug = selectedCidade ? slugify(selectedCidade) : "";

    if (cidadeSlug) {
      navigate(`/clinicas/${estadoSlug}/${cidadeSlug}`);
      return;
    }

    navigate(`/clinicas/${estadoSlug}`);
  };

  return (
    <section className="border-y bg-muted/30">
      <div className="max-w-5xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-bold text-foreground mb-2">
              Encontre uma clínica perto de você!
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Estrutura moderna e completa pensada para o bem-estar do paciente e da família.
              Selecione seu estado e cidade para ver as clínicas disponíveis na sua região.
            </p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="landing-estado" className="sr-only">Estado</Label>
              <select
                id="landing-estado"
                value={selectedEstado}
                onChange={(e) => {
                  setSelectedEstado(e.target.value);
                  setSelectedCidade("");
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="">Escolha o estado</option>
                {estados.map((e) => (
                  <option key={e.id} value={e.nome}>
                    {e.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="landing-cidade" className="sr-only">Cidade</Label>
              <select
                id="landing-cidade"
                value={selectedCidade}
                onChange={(e) => setSelectedCidade(e.target.value)}
                disabled={!selectedEstado || loading}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
              >
                <option value="">
                  {loading ? "Carregando cidades..." : "Escolha a cidade"}
                </option>
                {cidades.map((c) => (
                  <option key={c.id} value={c.nome}>
                    {c.nome}
                  </option>
                ))}
              </select>
            </div>
            <Button onClick={handleSearch} className="w-full gap-2">
              <MapPin className="h-4 w-4" /> Buscar Clínicas
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
